class HeroSlider extends HTMLElement {
  #touchStartX = 0;
  #handleVisibilityChange = () => {
    if (document.hidden) {
      this.stopAutoplay();
    } else {
      this.startAutoplay();
    }
  };

  constructor() {
    super();
    this.currentIndex = 0;
    this.autoplayTimer = null;
    this.isTransitioning = false;
  }

  connectedCallback() {
    this.track = this.querySelector('.hero-slider__track');
    this.slides = this.querySelectorAll('.hero-slider__slide');
    this.prevBtn = this.querySelector('.hero-slider__btn--prev');
    this.nextBtn = this.querySelector('.hero-slider__btn--next');
    this.dots = this.querySelectorAll('.hero-slider__dot');

    if (this.slides.length <= 1) return;

    this.prevBtn?.addEventListener('click', () => this.goTo(this.currentIndex - 1));
    this.nextBtn?.addEventListener('click', () => this.goTo(this.currentIndex + 1));

    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const index = parseInt(dot.dataset.index, 10);
        this.goTo(index);
      });
    });

    this.addEventListener('mouseenter', () => {
      if (this.dataset.pauseOnHover === 'true') this.stopAutoplay();
    });
    this.addEventListener('mouseleave', () => {
      if (this.dataset.pauseOnHover === 'true') this.startAutoplay();
    });
    this.addEventListener('keyup', (e) => {
      if (e.key === 'ArrowLeft') this.goTo(this.currentIndex - 1);
      if (e.key === 'ArrowRight') this.goTo(this.currentIndex + 1);
    });
    this.addEventListener('touchstart', (e) => this.#handleTouchStart(e), { passive: true });
    this.addEventListener('touchend', (e) => this.#handleTouchEnd(e), { passive: true });

    this.setAttribute('role', 'region');
    this.setAttribute('aria-roledescription', 'carousel');

    if (this.dataset.autoplay === 'true') this.startAutoplay();
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  disconnectedCallback() {
    this.stopAutoplay();
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  goTo(index) {
    if (this.isTransitioning) return;
    const total = this.slides.length;
    this.currentIndex = ((index % total) + total) % total;
    this.isTransitioning = true;
    this.stopAutoplay();
    this.startAutoplay();
    this.track.style.transform = `translateX(-${this.currentIndex * 100}%)`;

    this.dots.forEach((dot) => {
      dot.classList.toggle('hero-slider__dot--active', parseInt(dot.dataset.index, 10) === this.currentIndex);
      dot.setAttribute('aria-current', parseInt(dot.dataset.index, 10) === this.currentIndex ? 'true' : 'false');
    });

    this.slides.forEach((slide, i) => {
      slide.setAttribute('aria-hidden', i === this.currentIndex ? 'false' : 'true');
    });

    this.track.addEventListener(
      'transitionend',
      () => {
        this.isTransitioning = false;
      },
      { once: true }
    );
  }

  startAutoplay() {
    if (this.dataset.autoplay !== 'true' || this.slides.length <= 1) return;
    const speed = parseInt(this.dataset.speed, 10) * 1000 || 5000;
    this.stopAutoplay();
    this.autoplayTimer = setInterval(() => this.goTo(this.currentIndex + 1), speed);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  #handleTouchStart(e) {
    this.#touchStartX = e.changedTouches[0].screenX;
  }

  #handleTouchEnd(e) {
    const diff = this.#touchStartX - e.changedTouches[0].screenX;
    if (Math.abs(diff) > 50) {
      this.goTo(this.currentIndex + (diff > 0 ? 1 : -1));
    }
  }
}

if (!customElements.get('hero-slider')) {
  customElements.define('hero-slider', HeroSlider);
}
